<template>
  <small-modal
    :title="modalTitle"
    class="add-localization-modal"
    :fit-content="true"
    @close="close"
  >
    <div class="add-localization-modal-container">
      <h2>{{ $t("account.userInformation.addLocalizationModal.subtitle") }}</h2>

      <banner-modal
        class="banner-modal"
        :title="$t('deleteCampaign.information')"
        :text="bannerText"
      />

      <div v-if="hasSettings" class="selected-locales">
        <div
          v-for="(setting, codeKey) in languageSettings"
          :key="codeKey"
          class="language-item"
        >
          <button class="locale-btn">
            <img
              :src="getLocaleByCode(codeKey).flag"
              alt=""
              class="flag-icon"
            />
            <span class="locale-name">
              {{ getLocaleLabel(codeKey) }}
            </span>
            <button
              class="filter-remove-icon"
              @click.stop="removeLocale(codeKey)"
            >
              <SquaredX />
            </button>
          </button>

          <template v-if="isArraySetting(codeKey)">
            <button
              v-for="(filt, idx) in languageSettings[codeKey].filters"
              :key="idx"
              class="filter-btn"
              @click.stop="openFilterModal(codeKey, idx)"
            >
              URL {{ getComparisonLabel(filt.comparison) }}
              <span class="bold">{{ filt.string }}</span>
              <button
                class="filter-remove-icon"
                @click.stop="removeFilterInline(codeKey, idx)"
              >
                <SquaredX />
              </button>
            </button>
          </template>
          <template v-else>
            <button
              class="filter-btn"
              @click.stop="openFilterModal(codeKey, 0)"
            >
              URL {{ getComparisonLabel(languageSettings[codeKey].comparison) }}
              <span class="bold">{{ languageSettings[codeKey].string }}</span>
              <button
                class="filter-remove-icon"
                @click.stop="removeFilterInline(codeKey, 0)"
              >
                <SquaredX />
              </button>
            </button>
          </template>

          <button
            class="add-filter-btn"
            @click.stop="openAddFilterModal(codeKey)"
          >
            +
          </button>
        </div>
      </div>

      <div class="add-locale-section">
        <button class="add-locale-button" @click="toggleDropdown">
          {{ $t("account.userInformation.addLocalizationModal.add") }}
        </button>

        <div
          v-if="showDropdown"
          v-click-outside="closeLocaleDropdown"
          class="locale-dropdown"
        >
          <div
            v-for="locale in availableLocales"
            :key="locale.code"
            class="locale-item"
            @click="addLocale(locale)"
          >
            <img :src="locale.flag" alt="" class="flag-icon" />
            <span class="locale-name">{{
              locale.name[currentLang] || locale.name.en
            }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="buttons">
      <input
        class="button-primary --grey"
        type="button"
        :value="$t('deleteCampaign.cancel')"
        @click.prevent="close"
      />
      <label class="button-primary button-flex">
        <input
          class="submit-button"
          type="submit"
          :value="$t('create.bulk.modalButtons.confirm')"
          @click.prevent="submitLocaleSettings"
        />
      </label>
    </div>

    <div
      v-if="showFilterModal"
      v-click-outside="closeFilterModal"
      class="modal filter-edit-modal"
    >
      <div class="radio-group">
        <div
          v-for="(filter, idx) in filterForm.filters"
          :key="idx"
          class="filter-row"
        >
          <div class="radio-option">
            <input
              :id="`contains-${idx}`"
              v-model="filter.comparison"
              type="radio"
              value="contains"
              @change="updateFilter(idx)"
            />
            <label :for="`contains-${idx}`">{{
              $t("editCampaign.contains")
            }}</label>
          </div>
          <div v-show="filter.comparison === 'contains'" class="input-group">
            <input
              v-model="filter.string"
              class="text-input"
              type="text"
              placeholder="Enter value"
              @input="updateFilter(idx)"
              @keyup.enter="closeFilterModal"
            />
          </div>

          <div class="radio-option">
            <input
              :id="`not_contains-${idx}`"
              v-model="filter.comparison"
              type="radio"
              value="not_contains"
              @change="updateFilter(idx)"
            />
            <label :for="`not_contains-${idx}`">{{
              $t("editCampaign.notContain")
            }}</label>
          </div>
          <div
            v-show="filter.comparison === 'not_contains'"
            class="input-group"
          >
            <input
              v-model="filter.string"
              class="text-input"
              type="text"
              placeholder="Enter value"
              @input="updateFilter(idx)"
              @keyup.enter="closeFilterModal"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showAddFilterModal"
      v-click-outside="closeAddFilterModal"
      class="modal filter-edit-modal"
    >
      <div class="radio-group">
        <div class="filter-row">
          <div class="radio-option">
            <input
              id="add-contains"
              v-model="addFilterForm.comparison"
              type="radio"
              value="contains"
            />
            <label for="add-contains">{{ $t("editCampaign.contains") }}</label>
          </div>
          <div
            v-show="addFilterForm.comparison === 'contains'"
            class="input-group"
          >
            <input
              v-model="addFilterForm.string"
              class="text-input"
              type="text"
              placeholder="Enter value"
              @keyup.enter="closeAddFilterModal"
            />
          </div>

          <div class="radio-option">
            <input
              id="add-not_contains"
              v-model="addFilterForm.comparison"
              type="radio"
              value="not_contains"
            />
            <label for="add-not_contains">{{
              $t("editCampaign.notContain")
            }}</label>
          </div>
          <div
            v-show="addFilterForm.comparison === 'not_contains'"
            class="input-group"
          >
            <input
              v-model="addFilterForm.string"
              class="text-input"
              type="text"
              placeholder="Enter value"
              @keyup.enter="closeAddFilterModal"
            />
          </div>
        </div>
      </div>
    </div>
  </small-modal>
</template>

<script>
import SmallModal from "@/components/shared/SmallModal";
import BannerModal from "@/components/shared/BannerModal.vue";
import SquaredX from "@/assets/svg/squared-x-icon.svg?inline";
import deepClone from "@/utils/deepClone.js";
import { locales } from "@/utils/locales";
import { mapState } from "vuex";

export default {
  components: { SmallModal, BannerModal, SquaredX },
  data() {
    return {
      showDropdown: false,
      languageSettings: {},
      locales,

      showFilterModal: false,
      editingKey: null,
      editingIndex: 0,
      filterForm: { filters: [] },

      showAddFilterModal: false,
      addFilterKey: null,
      addFilterForm: { comparison: "contains", string: "" },
    };
  },
  computed: {
    ...mapState(["site"]),
    modalTitle() {
      return this.$t("account.userInformation.addLocalizationModal.title");
    },
    bannerText() {
      return this.$t("account.userInformation.addLocalizationModal.banner");
    },
    availableLocales() {
      return this.locales.filter(
        (l) => !this.languageSettings[l.code.replace(/\//g, "")]
      );
    },
    hasSettings() {
      return Object.keys(this.languageSettings).length > 0;
    },
    currentLang() {
      return this.$i18n.locale.split("-")[0];
    },
  },
  created() {
    if (this.site.site.languageSettings) {
      this.languageSettings = deepClone(this.site.site.languageSettings);
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    addLocale(locale) {
      const codeKey = locale.code.replace(/\//g, "");
      if (!this.languageSettings[codeKey]) {
        this.$set(this.languageSettings, codeKey, {
          comparison: "contains",
          string: locale.code,
        });
      }
      this.showDropdown = false;
    },
    removeLocale(codeKey) {
      this.$delete(this.languageSettings, codeKey);
    },
    getLocaleByCode(codeKey) {
      return (
        this.locales.find((l) => l.code.replace(/\//g, "") === codeKey) || {}
      );
    },
    getLocaleLabel(codeKey) {
      const locale = this.getLocaleByCode(codeKey);
      return locale.name[this.currentLang] || locale.name.en;
    },
    isArraySetting(codeKey) {
      return Array.isArray(this.languageSettings[codeKey].filters);
    },

    openFilterModal(codeKey, idx) {
      this.editingKey = codeKey;
      this.editingIndex = idx;
      const setting = this.languageSettings[codeKey];
      const base = Array.isArray(setting.filters)
        ? setting.filters[idx]
        : { comparison: setting.comparison, string: setting.string };
      this.filterForm.filters = [{ ...base }];
      this.showFilterModal = true;
    },
    closeFilterModal() {
      this.showFilterModal = false;
      this.editingKey = null;
      this.filterForm.filters = [];
    },
    updateFilter() {
      if (!this.editingKey) return;
      const f = this.filterForm.filters[0];
      const codeKey = this.editingKey;
      let setting = this.languageSettings[codeKey];
      if (Array.isArray(setting.filters)) {
        this.$set(setting.filters, this.editingIndex, { ...f });
      } else {
        setting = { comparison: f.comparison, string: f.string };
      }
      this.$set(this.languageSettings, codeKey, setting);
    },

    removeFilterInline(codeKey, idx) {
      let setting = this.languageSettings[codeKey];
      if (Array.isArray(setting.filters)) {
        setting.filters.splice(idx, 1);
        if (setting.filters.length === 1) {
          const sole = setting.filters[0];
          this.$set(this.languageSettings, codeKey, {
            comparison: sole.comparison,
            string: sole.string,
          });
        } else {
          this.$set(this.languageSettings, codeKey, setting);
        }
      } else {
        this.$delete(this.languageSettings, codeKey);
      }
    },

    openAddFilterModal(codeKey) {
      this.addFilterKey = codeKey;
      this.addFilterForm = { comparison: "contains", string: "" };
      this.showAddFilterModal = true;
    },
    closeAddFilterModal() {
      if (this.addFilterForm.string) {
        const codeKey = this.addFilterKey;
        let setting = this.languageSettings[codeKey];
        if (!Array.isArray(setting.filters)) {
          const prev = {
            comparison: setting.comparison,
            string: setting.string,
          };
          setting = { filters: [prev], type: "and" };
        }
        setting.filters.push({ ...this.addFilterForm });
        this.$set(this.languageSettings, codeKey, setting);
      }
      this.showAddFilterModal = false;
      this.addFilterKey = null;
      this.addFilterForm = { comparison: "contains", string: "" };
    },
    closeLocaleDropdown() {
      this.showDropdown = false;
    },
    async submitLocaleSettings() {
      const siteId = this.site.site._id;
      const dataToUpdate = { languageSettings: this.languageSettings };
      try {
        await this.$store.dispatch("site/updateSite", {
          siteId,
          dataToUpdate,
        });
        this.close();
      } catch (err) {
        console.error(err);
      }
    },
    getComparisonLabel(comparison) {
      return this.$t("advancedSegmentation." + comparison);
    },
  },
};
</script>
<style lang="scss">
.add-localization-modal .small-modal.fit-modal {
  width: 570px;
}
</style>

<style lang="scss" scoped>
.banner-modal {
  margin-bottom: 24px;
  min-width: 480px;
}
.add-localization-modal-container h2 {
  text-align: center;
  margin-bottom: 24px;
  font-weight: 400;
}

.selected-locales {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}
.language-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.locale-btn,
.filter-btn,
.add-filter-btn {
  @include base-font;
  @include font-normal;
  font-weight: 500;
  padding: 8px 16px;
  border: none;
  background-color: rgba(234, 234, 234, 1);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
}
.locale-btn,
.filter-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    .filter-remove-icon {
      display: flex;
      background-color: rgba(224, 224, 224, 1);
    }
  }
}
.bold {
  font-weight: 600;
}
.flag-icon {
  width: 16px;
  height: 16px;
  object-fit: cover;
  margin-right: 6px;
}
.locale-name {
  font-size: 14px;
}
.remove-icon {
  margin-left: 8px;
  font-size: 12px;
  cursor: pointer;
}
.filter-remove-icon {
  position: absolute;
  right: 0%;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(234, 234, 234, 1);
  cursor: pointer;
  display: none;
  align-items: center;
  padding: 2px 8px;
  z-index: 100;
  &::before {
    content: "";
    position: absolute;
    left: -8px;
    top: 0;
    bottom: 0;
    width: 8px;
    background-color: rgba(234, 234, 234, 1);
    filter: blur(6px);
    z-index: -1;
  }
}

.add-locale-section {
  position: relative;
}
.add-locale-button {
  @include base-font;
  @include font-normal;
  font-weight: 500;
  padding: 8px 16px;
  border: none;
  background-color: rgba(234, 234, 234, 1);
  border-radius: 2px;
  cursor: pointer;
}

.locale-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  max-height: 170px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  text-align: left;
}
.locale-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
}
.locale-item:hover {
  background: #f5f5f5;
}

.modal {
  position: absolute;
  bottom: 62px;
  background: white;
  padding: 12px;
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid rgba(196, 196, 196, 1);
  z-index: 100;
}
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.radio-option {
  display: flex;
  align-items: center;
  gap: 6px;
}
.input-group {
  margin-left: 24px;
}
.text-input {
  border: 1px solid rgba(196, 196, 196, 1);
  border-radius: 2px;
  padding: 4px 8px;
}
.buttons {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 72px;
}

.submit-button {
  @include base-font;
  @include font-small;
  font-weight: 500;
  background: transparent;
  outline: none;
  border: none;
  cursor: pointer !important;
}
</style>
